import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { X, Download, Smartphone } from 'lucide-react';
import { setupInstallPrompt, showInstallPrompt, isAppInstalled, getInstallInstructions } from '@/utils/pwaUtils';

const PWAInstallBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    // Don't show if already installed
    if (isAppInstalled()) {
      return;
    }

    // Setup install prompt listener
    setupInstallPrompt((installable) => {
      setIsInstallable(installable);
      
      // Show banner after 3 seconds if installable
      if (installable) {
        setTimeout(() => {
          const dismissed = localStorage.getItem('pwa-install-dismissed');
          if (!dismissed) {
            setShowBanner(true);
          }
        }, 3000);
      }
    });

    // Check if banner was dismissed recently (within 7 days)
    const dismissedTime = localStorage.getItem('pwa-install-dismissed-time');
    if (dismissedTime) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }
  }, []);

  const handleInstall = async () => {
    if (isInstallable) {
      const result = await showInstallPrompt();
      if (result.outcome === 'accepted') {
        setShowBanner(false);
      }
    } else {
      // Show manual instructions for iOS Safari and other browsers
      setShowInstructions(true);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
    localStorage.setItem('pwa-install-dismissed-time', Date.now().toString());
  };

  const instructions = getInstallInstructions();

  if (!showBanner && !showInstructions) {
    return null;
  }

  if (showInstructions) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
        <Card className="w-full max-w-md bg-white p-6 rounded-t-2xl sm:rounded-2xl animate-slide-up">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Install SkillEdge</h3>
                <p className="text-sm text-gray-600">{instructions.platform}</p>
              </div>
            </div>
            <button onClick={() => setShowInstructions(false)} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-3 mb-6">
            {instructions.steps.map((step, index) => (
              <div key={index} className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold">
                  {index + 1}
                </div>
                <p className="text-gray-700">{step}</p>
              </div>
            ))}
          </div>
          
          <Button onClick={() => setShowInstructions(false)} className="w-full">
            Got it!
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:bottom-4 sm:left-4 sm:right-auto animate-slide-up">
      <Card className="bg-gradient-to-r from-blue-600 to-blue-500 text-white p-4 shadow-2xl max-w-md">
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-white/80 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="flex items-start gap-4 pr-6">
          <div className="flex-shrink-0 w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-lg">
            <img 
              src="/pwa-icons/icon-96x96.png" 
              alt="SkillEdge" 
              className="w-12 h-12"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div style={{display: 'none'}} className="text-2xl font-bold text-blue-600">SE</div>
          </div>
          
          <div className="flex-1">
            <h3 className="font-bold text-lg mb-1">Install SkillEdge App</h3>
            <p className="text-sm text-white/90 mb-3">
              Get quick access and offline support. Install our app for the best experience!
            </p>
            
            <div className="flex gap-2">
              <Button 
                onClick={handleInstall}
                size="sm"
                className="bg-white text-blue-600 hover:bg-gray-100 font-semibold"
              >
                <Download className="w-4 h-4 mr-2" />
                Install Now
              </Button>
              <Button 
                onClick={handleDismiss}
                size="sm"
                variant="ghost"
                className="text-white hover:bg-white/10"
              >
                Maybe Later
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PWAInstallBanner;
